<div class="modal-header">
  <h4 class="modal-title">
    <i class="fa fa-user-plus text-primary me-2"></i>
    {{ 'manageEvent.addPackageRegistration.title' | translate }}
  </h4>
  <button type="button" class="btn-close" (click)="close()"></button>
</div>

<div class="modal-body">
  <!-- Package Info -->
  <div class="alert alert-light-primary d-flex align-items-center mb-5">
    <i class="fa fa-box-open fs-2x text-primary me-3"></i>
    <div>
      <strong>{{ package.package_name }}</strong>
      <span class="text-muted ms-2">{{ formatPrice(package.price_cents, package.currency) }}</span>
      <span class="text-muted ms-2">({{ package.session_count }} {{ 'manageEvent.addPackageRegistration.sessions' | translate }})</span>
    </div>
  </div>

  <!-- Member Type Selection -->
  <div class="member-type-selector mb-5">
    <div class="d-flex gap-3">
      <div
        class="type-option flex-fill"
        [class.active]="memberType === 'existing'"
        (click)="memberType = 'existing'; onMemberTypeChange()"
      >
        <i class="fa fa-user-check"></i>
        <span>{{ 'manageEvent.addPackageRegistration.existingUser' | translate }}</span>
        <small>{{ 'manageEvent.addPackageRegistration.existingUserDesc' | translate }}</small>
      </div>
      <div
        class="type-option flex-fill"
        [class.active]="memberType === 'manual'"
        (click)="memberType = 'manual'; onMemberTypeChange()"
      >
        <i class="fa fa-user-edit"></i>
        <span>{{ 'manageEvent.addPackageRegistration.manualEntry' | translate }}</span>
        <small>{{ 'manageEvent.addPackageRegistration.manualEntryDesc' | translate }}</small>
      </div>
    </div>
  </div>

  <!-- Error Message -->
  @if (errorMessage) {
    <div class="alert alert-danger mb-4">
      <i class="fa fa-exclamation-triangle me-2"></i>
      {{ errorMessage }}
    </div>
  }

  <!-- Existing User Search -->
  @if (memberType === 'existing') {
    <div class="form-group mb-5">
      <label class="form-label required">{{ 'manageEvent.addPackageRegistration.searchParticipant' | translate }}</label>
      @if (selectedUser) {
        <div class="selected-user-card">
          <div class="d-flex align-items-center">
            <div class="symbol symbol-40px me-3">
              <span class="symbol-label bg-light-primary text-primary fw-bold">
                {{ selectedUser.first_name?.charAt(0) || 'U' }}
              </span>
            </div>
            <div class="flex-grow-1">
              <div class="fw-bold">{{ selectedUser.full_name || selectedUser.first_name + ' ' + selectedUser.last_name }}</div>
              <div class="text-muted small">{{ selectedUser.email }}</div>
            </div>
            <button type="button" class="btn btn-sm btn-light-danger" (click)="clearSelectedUser()">
              <i class="fa fa-times"></i>
            </button>
          </div>
        </div>
      } @else {
        <div class="position-relative">
          <input
            type="text"
            class="form-control"
            [(ngModel)]="userSearchQuery"
            (ngModelChange)="onUserSearch()"
            [placeholder]="'manageEvent.addPackageRegistration.searchPlaceholder' | translate"
          />
          @if (isSearchingUsers) {
            <div class="search-spinner">
              <i class="fa fa-spinner fa-spin"></i>
            </div>
          }
        </div>
        @if (userSearchResults.length > 0) {
          <div class="search-results mt-2">
            @for (user of userSearchResults; track user.user_id) {
              <div class="search-result-item" (click)="selectUser(user)">
                <div class="symbol symbol-30px me-2">
                  <span class="symbol-label bg-light text-primary fw-bold">
                    {{ user.first_name?.charAt(0) || 'U' }}
                  </span>
                </div>
                <div>
                  <div class="fw-semibold">{{ user.full_name || user.first_name + ' ' + user.last_name }}</div>
                  <div class="text-muted small">{{ user.email }}</div>
                </div>
              </div>
            }
          </div>
        }
        @if (userSearchQuery.length >= 2 && userSearchResults.length === 0 && !isSearchingUsers) {
          <div class="text-muted small mt-2">
            <i class="fa fa-info-circle me-1"></i>
            {{ 'manageEvent.addPackageRegistration.noUsersFound' | translate:{ query: userSearchQuery } }}
          </div>
        }
      }
    </div>
  }

  <!-- Manual User Entry -->
  @if (memberType === 'manual') {
    <div class="row g-3 mb-5">
      <div class="col-6">
        <label class="form-label required">{{ 'manageEvent.addPackageRegistration.firstName' | translate }}</label>
        <input
          type="text"
          class="form-control"
          [(ngModel)]="firstName"
          [placeholder]="'manageEvent.addPackageRegistration.firstNamePlaceholder' | translate"
        />
      </div>
      <div class="col-6">
        <label class="form-label required">{{ 'manageEvent.addPackageRegistration.lastName' | translate }}</label>
        <input
          type="text"
          class="form-control"
          [(ngModel)]="lastName"
          [placeholder]="'manageEvent.addPackageRegistration.lastNamePlaceholder' | translate"
        />
      </div>
      <div class="col-6">
        <label class="form-label">{{ 'manageEvent.addPackageRegistration.email' | translate }}</label>
        <input
          type="email"
          class="form-control"
          [(ngModel)]="email"
          [placeholder]="'manageEvent.addPackageRegistration.emailPlaceholder' | translate"
        />
      </div>
      <div class="col-6">
        <label class="form-label">{{ 'manageEvent.addPackageRegistration.phone' | translate }}</label>
        <input
          type="tel"
          class="form-control"
          [(ngModel)]="phone"
          [placeholder]="'manageEvent.addPackageRegistration.phonePlaceholder' | translate"
        />
      </div>
    </div>
  }

  <hr class="my-5"/>

  <!-- Horse Selection -->
  <div class="mb-5">
    <div class="d-flex justify-content-between align-items-center mb-2">
      <label class="form-label mb-0">
        <i class="fa fa-horse text-muted me-2"></i>
        {{ 'manageEvent.addPackageRegistration.horseOptional' | translate }}
      </label>
      <button
        type="button"
        class="btn btn-sm btn-light-primary"
        (click)="openAddHorseModal()"
      >
        <i class="fa fa-plus me-1"></i>
        {{ 'manageEvent.addPackageRegistration.addNewHorse' | translate }}
      </button>
    </div>
    @if (loadingHorses) {
      <div class="d-flex align-items-center">
        <div class="spinner-border spinner-border-sm me-2" role="status"></div>
        <span class="text-muted">{{ 'manageEvent.addPackageRegistration.loadingHorses' | translate }}</span>
      </div>
    } @else {
      @if (availableHorses.length > 0) {
        <app-selectorini
          [options]="availableHorses"
          [displayKey]="'name'"
          [valueKey]="'horse_id'"
          [placeholder]="'manageEvent.addPackageRegistration.selectHorsePlaceholder' | translate"
          [value]="selectedHorse?.horse_id"
          (valueChange)="onHorseChanged($event)"
          [searchable]="true"
        />
        <div class="text-muted fs-7 mt-1">
          {{ 'manageEvent.addPackageRegistration.showingHorses' | translate:{ count: availableHorses.length } }}
        </div>
        @if (selectedHorse) {
          <div class="mt-3 p-3 bg-light rounded">
            <div class="d-flex align-items-center">
              <i class="fa fa-horse fs-2 text-primary me-3"></i>
              <div>
                <div class="fw-bold">{{ selectedHorse.name }}</div>
                @if (selectedHorse.breed) {
                  <div class="text-muted fs-7">{{ selectedHorse.breed }}</div>
                }
              </div>
              <button type="button" class="btn btn-sm btn-light ms-auto" (click)="selectedHorse = null">
                <i class="fa fa-times"></i>
              </button>
            </div>
          </div>
        }
      } @else {
        <div class="alert alert-light-warning">
          <i class="fa fa-info-circle me-2"></i>
          {{ 'manageEvent.addPackageRegistration.noHorsesFound' | translate }}
        </div>
      }
    }
  </div>

  <!-- Notes -->
  <div class="mb-5">
    <label class="form-label">{{ 'manageEvent.addPackageRegistration.notesOptional' | translate }}</label>
    <textarea
      class="form-control"
      rows="3"
      [placeholder]="'manageEvent.addPackageRegistration.notesPlaceholder' | translate"
      [(ngModel)]="notes"
    ></textarea>
  </div>

  <hr class="my-5"/>

  <!-- Payment Options -->
  <h6 class="mb-4">
    <i class="fa fa-credit-card text-muted me-2"></i>
    {{ 'manageEvent.addPackageRegistration.payment' | translate }}
  </h6>

  <!-- Payment Type Selection -->
  <div class="member-type-selector mb-4">
    <div class="d-flex gap-3">
      <div
        class="type-option flex-fill"
        [class.active]="paymentType === 'manual'"
        (click)="paymentType = 'manual'"
      >
        <i class="fa fa-hand-holding-dollar"></i>
        <span>{{ 'manageEvent.addPackageRegistration.manualPayment' | translate }}</span>
        <small>{{ 'manageEvent.addPackageRegistration.manualPaymentDesc' | translate }}</small>
      </div>
      <div
        class="type-option flex-fill"
        [class.active]="paymentType === 'send_link'"
        (click)="paymentType = 'send_link'"
      >
        <i class="fa fa-envelope"></i>
        <span>{{ 'manageEvent.addPackageRegistration.sendPaymentLink' | translate }}</span>
        <small>{{ 'manageEvent.addPackageRegistration.sendPaymentLinkDesc' | translate }}</small>
      </div>
    </div>
  </div>

  <!-- Manual Payment Options -->
  @if (paymentType === 'manual') {
    <div class="row g-3 mb-4">
      <div class="col-6">
        <label class="form-label">{{ 'manageEvent.addPackageRegistration.paymentStatus' | translate }}</label>
        <select class="form-select" [(ngModel)]="paymentStatus">
          <option value="paid">{{ 'manageEvent.addPackageRegistration.statusPaid' | translate }}</option>
          <option value="pay_later">{{ 'manageEvent.addPackageRegistration.statusPending' | translate }}</option>
          <option value="not_required">{{ 'manageEvent.addPackageRegistration.statusNotRequired' | translate }}</option>
        </select>
      </div>
      <div class="col-6">
        <label class="form-label">{{ 'manageEvent.addPackageRegistration.paymentMethod' | translate }}</label>
        <select class="form-select" [(ngModel)]="paymentMethod">
          <option value="cash">{{ 'manageEvent.addPackageRegistration.methodCash' | translate }}</option>
          <option value="bank_transfer">{{ 'manageEvent.addPackageRegistration.methodBankTransfer' | translate }}</option>
          <option value="other">{{ 'manageEvent.addPackageRegistration.methodOther' | translate }}</option>
        </select>
      </div>
    </div>
  }

  <!-- Send Payment Link Info -->
  @if (paymentType === 'send_link') {
    <div class="alert alert-light-info d-flex align-items-start mb-4">
      <i class="fa fa-info-circle fs-2 text-info me-3 mt-1"></i>
      <div>
        <div class="fw-bold mb-1">{{ 'manageEvent.addPackageRegistration.paymentLinkTitle' | translate }}</div>
        <div class="text-muted fs-7">
          {{ 'manageEvent.addPackageRegistration.paymentLinkDesc' | translate }}
        </div>
        @if (memberType === 'existing' && selectedUser?.email) {
          <div class="mt-2">
            <span class="badge badge-light-primary">{{ selectedUser?.email }}</span>
          </div>
        } @else if (memberType === 'manual' && email) {
          <div class="mt-2">
            <span class="badge badge-light-primary">{{ email }}</span>
          </div>
        } @else {
          <div class="text-danger mt-2 fs-7">
            <i class="fa fa-exclamation-triangle me-1"></i>
            {{ 'manageEvent.addPackageRegistration.emailRequired' | translate }}
          </div>
        }
      </div>
    </div>
  }
</div>

<div class="modal-footer">
  <app-button
    [variant]="'outline'"
    [label]="'common.cancel' | translate"
    (clicked)="close()"
  />
  <app-button
    [variant]="'primary'"
    [label]="submitting ? ('manageEvent.addPackageRegistration.adding' | translate) : ('manageEvent.addPackageRegistration.addRegistration' | translate)"
    [iconClass]="submitting ? 'fa fa-spinner fa-spin' : 'fa fa-plus'"
    [loading]="submitting"
    [disabled]="!isValid() || submitting"
    (clicked)="submit()"
  />
</div>
